/**
 * Owner: Masa Bayu
 * File: merge-dropcap-continuation.ts
 * Date:Time: 2026-10-21 16:40
 * Description: §448-S2 · dropcap baris-1 + continuation → satu perenggan mengalir.
 */

import type { TextElement } from '../extract/spsu-types';

const DROPCAP_PLATE = /^el_ocr_para_\d+_dropcap$/;
/** Same-column tolerance and max vertical gap (in line pitches) for a continuation. */
const COLUMN_X_TOLERANCE_PT = 4;
const MAX_GAP_PITCHES = 1;

/** Line fragments ending in a (soft) hyphen were split mid-word · glue the syllables. */
export function concatLineFragments(head: string, tail: string): string {
  const trimmedHead = head.replace(/\s+$/, '');
  if (/[-\u00AD]$/.test(trimmedHead)) return `${trimmedHead.slice(0, -1)}${tail}`;
  if (tail.startsWith(' ') || trimmedHead.endsWith(' ')) {
    return `${trimmedHead}${tail}`;
  }
  return `${trimmedHead} ${tail}`;
}

function pitchPt(el: TextElement): number {
  const lh = el.style.lineHeight ?? 1.2;
  return Math.max(1, el.style.fontSize * lh);
}

function sameTypography(a: TextElement, b: TextElement): boolean {
  return (
    a.bbox.unit === b.bbox.unit &&
    Math.abs(a.style.fontSize - b.style.fontSize) < 0.01 &&
    a.style.fontFamily === b.style.fontFamily
  );
}

function isContinuation(drop: TextElement, next: TextElement): boolean {
  if (next.id === drop.id || DROPCAP_PLATE.test(next.id)) return false;
  if (!next.id.startsWith('el_ocr_para_')) return false;
  if (next.linePaint !== undefined || drop.linePaint !== undefined) return false;
  if (!sameTypography(drop, next)) return false;
  if (Math.abs(next.bbox.x - drop.bbox.x) > COLUMN_X_TOLERANCE_PT) return false;
  const gap = next.bbox.y - (drop.bbox.y + drop.bbox.height);
  return gap >= 0 && gap <= MAX_GAP_PITCHES * pitchPt(drop);
}

function mergedStyle(
  drop: TextElement,
  next: TextElement,
): TextElement['style'] {
  /* Float ::first-letter sudah reserved lebar huruf jatuh · indent pertama retired. */
  const { textIndent: _retired, ...rest } = next.style;
  if (next.style.lineHeight === undefined && drop.style.lineHeight !== undefined) {
    return { ...rest, lineHeight: drop.style.lineHeight };
  }
  return rest;
}

export function mergeDropCapContinuations(
  elements: readonly TextElement[],
): readonly TextElement[] {
  const out: TextElement[] = [];
  const absorbed = new Set<string>();
  for (const drop of elements) {
    if (absorbed.has(drop.id)) continue;
    if (!DROPCAP_PLATE.test(drop.id)) {
      out.push(drop);
      continue;
    }
    const next = elements.find(
      (el) => el !== drop && !absorbed.has(el.id) && isContinuation(drop, el),
    );
    if (next === undefined) {
      out.push(drop);
      continue;
    }
    absorbed.add(next.id);
    const bottom = Math.max(
      drop.bbox.y + drop.bbox.height,
      next.bbox.y + next.bbox.height,
    );
    out.push({
      ...next,
      id: drop.id,
      content: concatLineFragments(drop.content, next.content),
      style: mergedStyle(drop, next),
      bbox: {
        x: Math.min(drop.bbox.x, next.bbox.x),
        y: drop.bbox.y,
        width: Math.max(drop.bbox.width, next.bbox.width),
        height: bottom - drop.bbox.y,
        unit: drop.bbox.unit,
      },
      layer: Math.max(drop.layer, next.layer),
    });
  }
  return out;
}
